import React from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import DownloadIcon from '@mui/icons-material/Download';
import Button from '@mui/material/Button';
import ReactGA from 'react-ga4';
import logo from '../assets/images/finaculator1.png';  
import addWatermark from './watermark';
import { useState } from "react";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

interface RecurringDepositPDFGeneratorProps {
  depositAmount: number;
  rate: number;
  period: number; 
  maturityAmount: number | null; 
  totalInterest: number | null; 
  periodUnit: 'years' | 'months';
  isCalculated?:boolean,

}

const RecurringDepositPDFGenerator: React.FC<RecurringDepositPDFGeneratorProps> = ({
  depositAmount,
  rate,
  period,
  maturityAmount,
  totalInterest,
  periodUnit,
  isCalculated
}) => {

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const generatePDF = () => {
    ReactGA.event({
      category: 'Recurring Deposit PDF',
      action: 'Recurring Deposit PDF Button',
      label: 'Recurring Deposit PDF',
    });

    
    if (!isCalculated) {
      setSnackbarMessage("Please calculate the values before downloading the PDF.");
      setSnackbarOpen(true);
      return;
    }
   

    if ( depositAmount <= 0 ||
      rate <= 0 ||
      period <= 0 ||
      maturityAmount === null ||
      totalInterest === null ||
      !periodUnit ||
      !isCalculated) {
      setSnackbarMessage("Please ensure all fields are filled before downloading the PDF.");
      setSnackbarOpen(true);
      return;
    }

    const doc = new jsPDF();
    const watermarkWidth = 100;  
    const watermarkHeight = 100; 
    const title = 'Recurring Deposit Investment Summary';
    const titleFontSize = 14;
    const bodyFontSize = 10;

    const verticalSpacing = 15; 
    const titleWidth = doc.getStringUnitWidth(title) * titleFontSize / doc.internal.scaleFactor;
    const pageWidth = doc.internal.pageSize.getWidth();
    const titleX = (pageWidth - titleWidth) / 2;

    doc.setFontSize(titleFontSize);
    doc.text(title, titleX, verticalSpacing);

    const inputDataRows = [
      ['Monthly Deposit Amount', `${Math.round(depositAmount).toLocaleString()}`],
      ['Annual Interest Rate', `${rate}%`],
      ['Time Period', `${period} ${periodUnit}`],
      ['Maturity Amount', `${maturityAmount ? Math.round(maturityAmount).toLocaleString() : 'N/A'}`],
      ['Total Interest Earned', `${totalInterest ? Math.round(totalInterest).toLocaleString() : 'N/A'}`],
    ];

    autoTable(doc, {
      head: [['Input Details', 'Values']],
      body: inputDataRows,
      startY: verticalSpacing + 20,
      headStyles: { fillColor: [22, 160, 133] },
      theme: 'grid',
      styles: { overflow: 'linebreak', cellWidth: 'auto', fontSize: bodyFontSize },
    });

    const breakdownData = getBreakdownData(depositAmount, rate, period, periodUnit); 

    const tableHeadings = periodUnit === 'months' 
      ? ['Month', 'Maturity Amount', 'Total Interest Earned'] 
      : ['Year', 'Maturity Amount', 'Total Interest Earned'];

    autoTable(doc, {
      head: [tableHeadings],
      body: breakdownData,
      startY: doc.autoTable.previous.finalY + 10, 
      headStyles: { fillColor: [22, 160, 133] },
      theme: 'grid',
      styles: { overflow: 'linebreak', cellWidth: 'auto', fontSize: bodyFontSize,halign:'center' },
    });

    addWatermark(doc, logo, watermarkWidth, watermarkHeight);
    const img = new Image();
    img.src = logo;
    img.onload = () => {
      const now = new Date();
      const padZero = (num: string | number) => ("0" + num).slice(-2);
      const formattedDate = `${now.getFullYear()}-${padZero(now.getMonth() + 1)}-${padZero(now.getDate())}`;
      const formattedTime = `${padZero(now.getHours())}-${padZero(now.getMinutes())}-${padZero(now.getSeconds())}`;
      const timestamp = `${formattedDate}_${formattedTime}`;
      const fileName = `finaculator_rd_calculator_${timestamp}.pdf`;
      doc.save(fileName);
    };
  };

  const getBreakdownData = (monthlyDeposit: number, interestRate: number, totalPeriods: number, periodUnit: 'years' | 'months') => {
    const breakdownData = [];
    const monthlyRate = interestRate / 100 / 12; 
    let totalDeposits = 0;
    let totalInterest = 0;
  
    const totalMonths = periodUnit === 'years' ? totalPeriods * 12 : totalPeriods; 
  
    for (let month = 1; month <= totalMonths; month++) {
      totalDeposits += monthlyDeposit; 
  
      const interestForMonth = (totalDeposits + totalInterest) * monthlyRate; 
      totalInterest += interestForMonth; 
  
      if (periodUnit === 'months') {
        const maturityAmountForMonth = totalDeposits + totalInterest; 
        breakdownData.push([
          month.toString(),
          `${Math.round(maturityAmountForMonth).toLocaleString()}`, 
          `${Math.round(totalInterest).toLocaleString()}`
        ]);
      } 
      else if (month % 12 === 0) {
        const year = month / 12; 
        const maturityAmountForYear = totalDeposits + totalInterest; 
        breakdownData.push([
          year.toString(),
          `${Math.round(maturityAmountForYear).toLocaleString()}`, 
          `${Math.round(totalInterest).toLocaleString()}`
        ]);
      }
    }
  
    return breakdownData;
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
      <Button
        variant="contained"
        color="success"
        onClick={generatePDF}
        style={{ marginTop: -70 }} 
        startIcon={<DownloadIcon />}
      >
        Download PDF
      </Button>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }} 
      >
        <MuiAlert onClose={handleSnackbarClose} severity="warning" sx={{ width: '100%' }}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </div>
  );
};

export default RecurringDepositPDFGenerator;
